// MechanicHint — the FM26-validated pattern: explain a game mechanic
// with a light animated vignette plus 2-3 bullet points.

import { useState, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HelpCircle, X } from 'lucide-react'
import { useMotionVariants, modalVariants, backdropVariants } from '../motion/motionTokens'

export interface MechanicHintProps {
  title: string
  bullets: string[] // 2-3 short explanations
  vignette?: ReactNode // optional animated SVG illustration
  inline?: boolean // render trigger as inline icon (default) or block link
}

/** Default vignette: pulsing demand bars */
function DefaultVignette() {
  const bars = [0.45, 0.75, 0.6]
  return (
    <svg viewBox="0 0 120 48" style={{ width: '100%', maxWidth: 200, height: 48 }} aria-hidden>
      {bars.map((height, i) => (
        <motion.rect
          key={i}
          x={12 + i * 36}
          width={24}
          rx={3}
          fill="var(--gold)"
          initial={{ height: 8, y: 40 }}
          animate={{ height: 40 * height, y: 44 - 40 * height }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            repeatType: 'reverse',
            delay: i * 0.25,
            ease: 'easeInOut',
          }}
        />
      ))}
    </svg>
  )
}

export function MechanicHint({ title, bullets, vignette, inline = true }: MechanicHintProps) {
  const [open, setOpen] = useState(false)
  const modal = useMotionVariants(modalVariants)
  const backdrop = useMotionVariants(backdropVariants)

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={title}
        aria-label={`Como funciona: ${title}`}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          display: inline ? 'inline-flex' : 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          padding: 0,
          fontSize: '0.72rem',
          verticalAlign: 'middle',
        }}
      >
        <HelpCircle size={14} />
        {!inline && <span>Como funciona</span>}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="modal-backdrop"
              variants={backdrop}
              initial="initial"
              animate="enter"
              exit="exit"
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="modal-card"
              variants={modal}
              initial="initial"
              animate="enter"
              exit="exit"
              // .modal-card centres via CSS `transform: translate(-50%,-50%)`, but
              // framer's scale animation overwrites that transform — so the card
              // lost its centring and spilled off the right edge on mobile. Re-add
              // the centring offsets as framer values so they compose with scale.
              style={{ maxWidth: 360, x: '-50%', y: '-50%' }}
              role="dialog"
              aria-label={title}
            >
              <div className="modal-header">
                <span className="card-title" style={{ flex: 1 }}>{title}</span>
                <button className="icon-btn" onClick={() => setOpen(false)} aria-label="Cerrar">
                  <X size={14} />
                </button>
              </div>
              <div className="modal-body">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
                  {vignette ?? <DefaultVignette />}
                </div>
                <ul style={{ paddingLeft: '1.1rem', display: 'grid', gap: '0.4rem' }}>
                  {bullets.map((bullet, i) => (
                    <li key={i} style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
